import {
  EMBED_AUTO_HEIGHT,
  EMBED_V2_PARAMS,
  EMBED_V2_VALUE,
  GA_LINKER_PARAM,
  WIDGET_V1_PARAM,
  type EmbedMode,
} from '@embed-protocol';
import { getEmbedParams, ownFrameId } from './iframeResize';

/**
 * How this page was reached, decided once from the entry URL: whether it is
 * embedded, by what, on which site, and what that allows.
 *
 * The entry query string is gone after the first `navigate()` and after an
 * auth round trip, so the answer is kept per window and in session storage,
 * keyed by `frameId` like the resize reporter's own store.
 */
export interface EmbedContext {
  mode: EmbedMode;
  /** Host of the embedding site, or null when unknown. */
  host: string | null;
  /** The parent handed us its GA identity through the linker (`_gl`). */
  hasParentIdentity: boolean;
  /** Whether the frame reports its height and lifts the viewport floor. */
  autoHeight: boolean;
  isV2: boolean;
  /** Language the restaurant fixed for its v2 widget, or null to follow the guest. */
  lang: string | null;
}

const DIRECT: EmbedContext = {
  mode: 'direct',
  host: null,
  hasParentIdentity: false,
  autoHeight: false,
  isV2: false,
  lang: null,
};

const CONTEXT_STORE_KEY = 'embed:context';
const MAX_STORED_CONTEXTS = 4;

const EMBED_MODES: EmbedMode[] = [
  'direct',
  'widget_v1',
  'widget_v2_inline',
  'widget_v2_floating',
  'external_iframe',
];

/** Params only the embedder's own src carries, so their presence means a fresh answer. */
const MODE_SIGNALS = [EMBED_V2_PARAMS.embed, WIDGET_V1_PARAM];

const liveContexts = new WeakMap<Window, EmbedContext>();
const pendingFreshStart = new WeakSet<Window>();

function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host || null;
  } catch {
    return null;
  }
}

function isMeitreHost(host: string): boolean {
  return /(^|\.)(meitre|meitre-test|meitre-staging)\.com$/.test(host) || host.startsWith('localhost');
}

/**
 * `parentOrigin` is validated and survives auth; `ancestorOrigins` is exact but
 * missing in Firefox; the referrer is the last resort and, after an auth round
 * trip, points at our own auth origin — which says nothing about the embedder.
 */
function resolveHost(win: Window, parentOrigin: string | null): string | null {
  const fromParent = hostOf(parentOrigin);
  if (fromParent) return fromParent;

  const ancestors = win.location.ancestorOrigins;
  const fromAncestors = ancestors && ancestors.length > 0 ? hostOf(ancestors[0]) : null;
  if (fromAncestors) return fromAncestors;

  const fromReferrer = hostOf(win.document.referrer);
  return fromReferrer && !isMeitreHost(fromReferrer) ? fromReferrer : null;
}

function deriveContext(win: Window, params: URLSearchParams, parentOrigin: string | null): EmbedContext {
  const isV2 = params.get(EMBED_V2_PARAMS.embed) === EMBED_V2_VALUE;
  const lang = params.get(EMBED_V2_PARAMS.lang);

  let mode: EmbedMode = 'external_iframe';
  if (isV2) {
    mode = params.get(EMBED_V2_PARAMS.mode) === 'floating' ? 'widget_v2_floating' : 'widget_v2_inline';
  } else if (params.get(WIDGET_V1_PARAM) === 'true') {
    mode = 'widget_v1';
  }

  return {
    mode,
    host: resolveHost(win, parentOrigin),
    hasParentIdentity: params.has(GA_LINKER_PARAM),
    autoHeight: isV2 ? params.get(EMBED_V2_PARAMS.height) === EMBED_AUTO_HEIGHT : true,
    isV2,
    lang: isV2 && /^[a-z]{2}$/.test(lang ?? '') ? lang : null,
  };
}

function readStore(win: Window): Record<string, EmbedContext> {
  try {
    const raw = win.sessionStorage?.getItem(CONTEXT_STORE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, EmbedContext>;
  } catch {
    return {};
  }
}

function isContext(value: unknown): value is EmbedContext {
  const v = value as Partial<EmbedContext> | null;
  return (
    !!v &&
    typeof v === 'object' &&
    EMBED_MODES.includes(v.mode as EmbedMode) &&
    (v.host === null || typeof v.host === 'string') &&
    typeof v.hasParentIdentity === 'boolean' &&
    typeof v.autoHeight === 'boolean' &&
    typeof v.isV2 === 'boolean' &&
    (v.lang === null || typeof v.lang === 'string')
  );
}

function remember(win: Window, key: string, context: EmbedContext) {
  try {
    const store = readStore(win);
    delete store[key];
    store[key] = context;
    const keys = Object.keys(store);
    for (const stale of keys.slice(0, Math.max(0, keys.length - MAX_STORED_CONTEXTS))) {
      delete store[stale];
    }
    win.sessionStorage?.setItem(CONTEXT_STORE_KEY, JSON.stringify(store));
  } catch {
    // Unavailable storage only loses the answer across a full page load.
  }
}

/**
 * The embed context of this window. Call it once before the first navigation
 * (main.tsx does) so it is read from the entry URL.
 */
export function getEmbedContext(win: Window = window): EmbedContext {
  if (win.self === win.top) return DIRECT;

  const cached = liveContexts.get(win);
  if (cached) return cached;

  const params = new URLSearchParams(win.location.search);
  const { parentOrigin } = getEmbedParams(win);
  // Frames without an id of their own (the v1 widget, a bare iframe) cannot be
  // told apart in the store, so each derives from its own URL; the v1 widget's
  // mark rides the auth round trip on the URL instead (applyEmbedModeParams).
  const key = ownFrameId(win);
  const stored = key ? readStore(win)[key] : undefined;
  let context: EmbedContext;
  if (MODE_SIGNALS.some((name) => params.has(name)) || !isContext(stored)) {
    context = deriveContext(win, params, parentOrigin);
    if (params.get(EMBED_V2_PARAMS.fresh) === '1') pendingFreshStart.add(win);
  } else {
    // A return from auth carries only frameId/parentOrigin: the mode is the
    // one the entry load recorded for this frame.
    context = { ...stored, hasParentIdentity: stored.hasParentIdentity || params.has(GA_LINKER_PARAM) };
  }

  if (key) remember(win, key, context);
  liveContexts.set(win, context);
  return context;
}

/** True exactly once, when the v2 script opened this frame asking for a fresh start. */
export function consumeFreshStart(win: Window = window): boolean {
  getEmbedContext(win);
  if (!pendingFreshStart.has(win)) return false;
  pendingFreshStart.delete(win);
  return true;
}

/**
 * Copies what an id-less frame needs to be recognised again after the auth
 * round trip: only the v1 widget's own mark. A bare iframe comes back as a
 * plain external one (without the parent's GA identity), a known limit of
 * embedding without frameId — the v2 widget is the way out of it.
 */
export function applyEmbedModeParams(target: URLSearchParams | Record<string, string>, win: Window = window): void {
  if (getEmbedContext(win).mode !== 'widget_v1') return;
  if (target instanceof URLSearchParams) target.set(WIDGET_V1_PARAM, 'true');
  else target[WIDGET_V1_PARAM] = 'true';
}

/**
 * Restaurant GA/GTM/pixel measure only where the result belongs to the
 * restaurant's own visitor: a direct visit, or an iframe whose parent handed
 * over its GA identity. Anywhere else they would count the same guest as a
 * second, campaign-less user — the v2 script measures from the site instead.
 */
export function allowsRestaurantTags(context: EmbedContext): boolean {
  return context.mode === 'direct' || (context.mode === 'external_iframe' && context.hasParentIdentity);
}

/**
 * Copies the v2 embed mode onto an outgoing URL. The login page lives on
 * another origin whose storage holds no context, so without these it would
 * take a v2 frame for a raw iframe: height patches on, ESC not relayed.
 */
export function applyEmbedV2Params(target: URLSearchParams | Record<string, string>, win: Window = window): void {
  const context = getEmbedContext(win);
  if (!context.isV2) return;
  const set = (key: string, value: string) => {
    if (target instanceof URLSearchParams) target.set(key, value);
    else target[key] = value;
  };
  set(EMBED_V2_PARAMS.embed, EMBED_V2_VALUE);
  set(EMBED_V2_PARAMS.mode, context.mode === 'widget_v2_floating' ? 'floating' : 'inline');
  if (context.autoHeight) set(EMBED_V2_PARAMS.height, EMBED_AUTO_HEIGHT);
  if (context.lang) set(EMBED_V2_PARAMS.lang, context.lang);
}
